import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Col, Input, Label, Row, Table, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { get, post } from "../../../../utils/helpers/api";
import FixedSave from "../../../../components/Actions/FixedSave";
import DefaultButton from "../../../../components/Buttons/Default";

const emptyRow = () => ({ status: '', check_in_time: '', check_out_time: '' });

function BulkCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [employeesData, setEmployeesData] = useState([]);
  const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
  const [rows, setRows] = useState({});
  const [search, setSearch] = useState('');
  const [defaultCheckIn, setDefaultCheckIn] = useState('09:00');
  const [defaultCheckOut, setDefaultCheckOut] = useState('17:00');
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);

  // backend returns "HH:mm:ss", <input type="time"> expects "HH:mm"
  const toTimeInput = time => time ? time.split(':').slice(0, 2).join(':') : '';

  // <input type="time"> returns "HH:mm", backend expects "HH:mm:ss"
  const toTimeWithSeconds = time => {
    if (!time) return null;
    return time.split(':').length === 2 ? `${time}:00` : time;
  };

  const getEmployeesData = async () => {
    setLoading(true);
    const data = await get(`/hr/attendance/bulk-create?attendance_date=${attendanceDate}`);
    if (data?.success) {
      setEmployeesData(data?.employees || []);

      // prefill rows with the attendance already recorded for that date
      const existing = {};
      (data?.attendances || []).forEach(attendance => {
        existing[attendance.employee_id] = {
          status: attendance.status || '',
          check_in_time: toTimeInput(attendance.check_in_time),
          check_out_time: toTimeInput(attendance.check_out_time)
        };
      });
      setRows(existing);
    }
    setLoading(false);
  };

  useEffect(() => {
    getEmployeesData();
  }, [attendanceDate]);

  const getRow = employeeId => rows[employeeId] || emptyRow();

  const updateRow = (employeeId, values) => {
    setRows(prev => ({
      ...prev,
      [employeeId]: { ...getRow(employeeId), ...values }
    }));
  };

  const onStatusChange = (employeeId, status) => {
    if (status === 'present') {
      const row = getRow(employeeId);
      updateRow(employeeId, {
        status,
        check_in_time: row.check_in_time || defaultCheckIn,
        check_out_time: row.check_out_time || defaultCheckOut
      });
    } else {
      updateRow(employeeId, { status, check_in_time: '', check_out_time: '' });
    }
  };

  const markAll = status => {
    const next = {};
    filteredEmployees.forEach(employee => {
      next[employee.id] = status === 'present'
        ? { status, check_in_time: defaultCheckIn, check_out_time: defaultCheckOut }
        : { status, check_in_time: '', check_out_time: '' };
    });
    setRows(prev => ({ ...prev, ...next }));
  };

  const clearAll = () => setRows({});

  const totalHours = row => {
    if (!row.check_in_time || !row.check_out_time) return '';
    const hours = (new Date(`1970-01-01T${row.check_out_time}`) - new Date(`1970-01-01T${row.check_in_time}`)) / 36e5;
    return hours > 0 ? hours.toFixed(2) : '';
  };

  const employeeName = employee => [employee.firstname, employee.middlename, employee.lastname].filter(Boolean).join(' ');

  const filteredEmployees = employeesData.filter(employee => {
    if (!search) return true;
    return employeeName(employee).toLowerCase().includes(search.toLowerCase());
  });

  const filledRows = Object.entries(rows).filter(([, row]) => row.status);

  const saveAttendance = async () => {
    setSaveLoading(true);
    const data = await post('/hr/attendance/bulk-store', {
      attendance_date: attendanceDate,
      attendances: filledRows.map(([employeeId, row]) => ({
        employee_id: employeeId,
        status: row.status,
        check_in_time: row.status === 'present' ? toTimeWithSeconds(row.check_in_time) : null,
        check_out_time: row.status === 'present' ? toTimeWithSeconds(row.check_out_time) : null
      }))
    });

    if (data.success) {
      navigate('/hr/attendance');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={['Attendance', 'Bulk Attendance']}
          onBack={() => navigate('/hr/attendance')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="3" className="mb-3">
              <Label>Attendance Date</Label>
              <Input
                type="date"
                value={attendanceDate}
                onChange={e => setAttendanceDate(e.target.value)}
              />
            </Col>
            <Col md="3" className="mb-3">
              <Label>Default Check In</Label>
              <Input
                type="time"
                value={defaultCheckIn}
                onChange={e => setDefaultCheckIn(e.target.value)}
              />
            </Col>
            <Col md="3" className="mb-3">
              <Label>Default Check Out</Label>
              <Input
                type="time"
                value={defaultCheckOut}
                onChange={e => setDefaultCheckOut(e.target.value)}
              />
            </Col>
            <Col md="3" className="mb-3">
              <Label>{t('common.search')}</Label>
              <Input
                type="text"
                value={search}
                placeholder={t('hr.employeeName')}
                onChange={e => setSearch(e.target.value)}
              />
            </Col>
          </Row>

          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <DefaultButton color="primary" outline size="sm" onClick={() => markAll('present')}>
              Mark All Present
            </DefaultButton>
            <DefaultButton color="secondary" outline size="sm" onClick={() => markAll('absent')}>
              Mark All Absent
            </DefaultButton>
            <DefaultButton color="secondary" outline size="sm" onClick={() => markAll('leave')}>
              Mark All Leave
            </DefaultButton>
            <DefaultButton color="dark" outline size="sm" onClick={clearAll}>
              Clear
            </DefaultButton>
            <span className="text-muted ms-auto small">{filledRows.length} / {employeesData.length}</span>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner size="sm" />
            </div>
          ) : (
            <Table bordered>
              <thead>
                <tr>
                  <th>{t('hr.employeeName')}</th>
                  <th width="160px">{t('common.status')}</th>
                  <th width="150px">Check In</th>
                  <th width="150px">Check Out</th>
                  <th width="120px">Total Hours</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map(employee => {
                  const row = getRow(employee.id);
                  return (
                    <tr key={employee.id}>
                      <td>
                        <div>{employeeName(employee)}</div>
                        {employee.job_title && <small className="text-muted">{employee.job_title}</small>}
                      </td>
                      <td>
                        <Input
                          type="select"
                          value={row.status}
                          onChange={e => onStatusChange(employee.id, e.target.value)}
                        >
                          <option value="">Select Status</option>
                          <option value="present">Present</option>
                          <option value="absent">Absent</option>
                          <option value="leave">Leave</option>
                        </Input>
                      </td>
                      <td>
                        <Input
                          type="time"
                          value={row.check_in_time}
                          disabled={row.status !== 'present'}
                          onChange={e => updateRow(employee.id, { check_in_time: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input
                          type="time"
                          value={row.check_out_time}
                          disabled={row.status !== 'present'}
                          onChange={e => updateRow(employee.id, { check_out_time: e.target.value })}
                        />
                      </td>
                      <td>
                        <Input type="text" value={totalHours(row)} readOnly />
                      </td>
                    </tr>
                  );
                })}
                {filteredEmployees.length === 0 && (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-3">
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <FixedSave
        loading={saveLoading}
        disabled={saveLoading || loading || filledRows.length === 0}
        onSave={saveAttendance}
      />
    </SidebarLayout>
  );
}

export default BulkCreatePage;
